'use client';

import { useEffect } from 'react';
import { ensureVisitorId } from '@/lib/api/analytics/visitor';
import { applyConsent, capture, loadPostHog, register } from './client';
import { isPostHogEnabled } from './config';
import { onAnalyticsConsentChange } from './consent';

/**
 * Boots PostHog once per page load. Mounted from `RootProviders`, which every
 * zone renders, so a single mount covers web, news, trade and auth.
 *
 * Renders nothing — PostHog state lives in the module singleton in `client.ts`,
 * not in React context, so consumers call `capture()` directly instead of
 * threading a provider through the tree.
 */
export function PostHogInit() {
  useEffect(() => {
    if (!isPostHogEnabled()) return;

    // Stamp every event with the in-house anonymous id so PostHog and the
    // analytics service can be joined on the same key. `ensureVisitorId` is
    // idempotent; calling it here means the cookie now exists for every visitor
    // rather than only those who reach an event or news page.
    const visitorId = ensureVisitorId();
    if (visitorId) register({ visitor_id: visitorId });

    void loadPostHog();

    return onAnalyticsConsentChange((consent) => {
      // Record the change itself. On withdrawal the event has to go out before
      // the opt-out lands, or it is the one event we can never see.
      if (consent === 'granted') {
        applyConsent(true);
        capture('analytics_consent_changed', { consent });
      } else {
        capture('analytics_consent_changed', { consent });
        applyConsent(false);
      }
    });
  }, []);

  return null;
}
